import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { getLogInUrl } from '@townsquare/auth-utils';
import { getConfig } from '@townsquare/config';
import { redirect as redirectFacade, location } from '@townsquare/facade';
import { useUserActions } from '@townsquare/user-store';

import { FullScreenSpaLoader } from '../ui/FullScreenSpaLoader';

import { prefetchedAuthCheckPromise } from './prefetch';

export const AuthCheck = ({ children }: React.PropsWithChildren<Record<never, never>>) => {
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const userActions = useUserActions();

  /**
   * Wrapper around the redirect facade to set the redirecting state,
   * this way we avoid some race conditions.
   */
  const redirect = (url: string) => {
    setIsRedirecting(true);
    redirectFacade(url);
  };

  const hacUrl = useMemo(() => getConfig().fullUrl, []);
  const checkHasCorrectAuth = useCallback(async () => {
    try {
      const { isAuthed, isUnauthenticated, isUnauthorised, isNoUser } = await prefetchedAuthCheckPromise;

      if (isAuthed) {
        // User is authenticated
        return;
      } else if (isUnauthorised) {
        const redirectUrl = new URL(hacUrl);
        if (isNoUser && location().pathname !== redirectUrl.pathname) {
          redirect(hacUrl);
        }
      } else if (isUnauthenticated) {
        redirect(getLogInUrl());
      } else {
        // Error code other than 401
        return redirect(hacUrl);
      }
    } finally {
      // Start loading the user & bootstrap query ref after we've checked auth
      if (!isRedirecting) {
        import('../bootstrap/preload');
        await userActions.initMe();
        setHasCheckedAuth(true);
      }
    }
  }, [hacUrl, isRedirecting, userActions]);

  useEffect(() => {
    void checkHasCorrectAuth();
  }, [checkHasCorrectAuth]);

  if (!hasCheckedAuth || isRedirecting) {
    return <FullScreenSpaLoader />;
  }

  return <>{children}</>;
};
